import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormlyType } from '../ga-formly.service';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { FormattingService } from '../../services/formatting.service';
import * as _ from 'underscore';
import { NgOption, NgSelectComponent } from '@ng-select/ng-select';
import { from, of, Subject } from 'rxjs';
import { ExtraFieldType } from '../extra-field-type';
import { compareFn, GaFormlyUtil } from '../ga-formly.util';

const NG_SELECT_OPTION_SELECTOR:string = '.ng-dropdown-panel .ng-dropdown-panel-items .ng-option';

@Component({
  selector: 'formly-field-ng-select',
  template: `
    <ng-select
      [ngClass]="classNames" 
      [formControl]="formControl" 
      [formlyAttributes]="field"
      [compareWith]="compareWithFunction"
      [inputFocus]="to.inputFocus"
      [trackByFn]="trackByFunction"
      [items]="to.groupProp ? (to.optionsObserver | async) : (to.options | formlySelectOptions: field | async)"
      [virtualScroll]="true"
      [searchable]="true"
      [multiple]="to.multiple"
      [clearable]="!to.multiple"
      [addTag]="to.multiple && to.addFunction"
      [typeahead]="to.searchFunction ? inputSubject : undefined"
      [minTermLength]="to.searchFunction ? to.minSearchLength : undefined"
      [loading]="to.loading"
      [groupBy]="to.groupProp"
      [bindValue]="valueProp"
      [typeToSearchText]="to.minSearchLength ? 'Please enter ' + to.minSearchLength + ' or more characters' : undefined"
      [placeholder]="to.placeholder"
      (clear)="onClear($event)"
      (open)="onDropdownOpenScroll()"
      (close)="onDropdownClose()"
      (scroll)="onDropdownOpenScroll()"
      appendTo="body"
      #select>
      <!-- option template -->
      <ng-template ng-option-tmp let-item="item" let-index="index">
        <span [innerHTML]="getLabel(item)"></span>
      </ng-template>
      
      <!-- label template, this template used when option is selected -->
      <ng-template ng-label-tmp let-item="item">
        <span [innerHTML]="formatSelectedOption(item)" *ngIf="to.parseHtml; else regularSelectedOption"></span>
        
        <ng-template #regularSelectedOption>
          <span>{{getLabel(item)}}</span>
        </ng-template>
      </ng-template>
      
      <!-- label template for multiselect, this template used when option is selected -->
      <ng-template ng-multi-label-tmp let-items="items" let-clear="clear">
        <div class="ng-value" *ngFor="let item of items">
          <span class="ng-value-icon left" (click)="clear(item)" aria-hidden="true"><i class="fa fa-times"></i></span>
          <span class="ng-value-label" [innerHTML]="formatSelectedOption(item.label)" *ngIf="to.parseHtml; else regularSelectedOption"></span>

          <ng-template #regularSelectedOption>
            <span class="ng-value-label">{{item.label}}</span>
          </ng-template>
        </div>
      </ng-template>
      
      <!-- Template for tag -->
      <ng-template ng-tag-tmp let-search="searchTerm">
        <div class="ng-select-tag-button" *ngIf="to.addFunction; else defaultTagTemplate">
          <multiselect-new-option [label]="to.addFunction?.label" (onClick)="to.addFunction?.onClick(search)"></multiselect-new-option>
        </div>
        
        <ng-template #defaultTagTemplate>
          <div class="ng-select-tag-button"><button class="btn btn-block btn-primary btn-sm"><strong>Add item</strong> "{{search}}"</button></div>
        </ng-template>
      </ng-template>
    </ng-select>
  `
})
export class FormlyFieldNgSelectComponent extends ExtraFieldType implements OnInit, OnDestroy {
  public defaultOptions = {
    templateOptions: {
      options: []
    }
  };
  public valueProp: string;
  public inputSubject: Subject<string> = new Subject<string>();
  @ViewChild(NgSelectComponent) private ngSelectRef: NgSelectComponent;

  constructor(
    private formattingService: FormattingService,
    private formlyUtil: GaFormlyUtil
  ) {
    super();
  }

  public ngOnInit(): void {
    this.formlyUtil.initSelectField(this.to, this.formControl);
    this.classNames = this.formlyUtil.createClassNames(FormlyType.NG_SELECT, <string>this.key, this.to.label);

    // when group prop is defined we should use what defined in value prop
    // because we are not formatting the option
    this.valueProp = this.to.groupProp ? <string>this.to.valueProp : 'value';

    // register listener
    this.listen();
  }

  protected listen(): void {
    super.listen();

    this.formControl.valueChanges.pipe(
      distinctUntilChanged(_.isEqual)
    ).subscribe(() => {
      // handle option behavior on option or model change
      this.onOptionsAndModelChange();
    });

    this.to.optionsObserver
      .pipe(
        distinctUntilChanged(_.isEqual)
      )
      .subscribe((options:any[]) => {
        // handle option behavior on option or model change
        this.onOptionsAndModelChange();

        if (this.to.translateOptions) {
          this.to.options = this.formlyUtil.translateOptions(options);
        }
      });

    // when it's multiple and use remote search to fetch option list
    if (this.to.multiple && !this.to.localSearch) {
      this.inputSubject.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.to.loading = true),
        switchMap((term: string) => {
          if (!term || (this.to.minSearchLength && term?.length < this.to.minSearchLength)) {
            this.to.loading = false;
            return of([]);
          }

          // check if its promise then it will be converted to observable
          return from(this.to.searchFunction.request(term))
            .pipe(
              catchError(() => of ([])),
              tap((res: any) => {
                this.to.options = res;
                this.to.loading = false;
              })
            );
        })
      ).subscribe();
    }
  }

  /**
   * @description workaround when ng-select has virtual scroll and option label should have multilines we should do this AFTER dropdown has been fully rendered
   * so ng-select won't recalculate the height and add unnecessary extra white lines at the end of dropdown options.
   * @reference https://github.com/ng-select/ng-select/issues/1941
   */
  public onDropdownOpenScroll():void {
    // add timeout here to wait until options are fully rendered
    setTimeout(() => {
      $(NG_SELECT_OPTION_SELECTOR).css('whiteSpace', 'normal');
    });
  }

  /**
   * @description workaround when ng-select has virtual scroll and option label should have multilines we should restore the default style when dropdown is closed
   * @reference https://github.com/ng-select/ng-select/issues/1941
   */
  public onDropdownClose():void {
    $(NG_SELECT_OPTION_SELECTOR).css('whiteSpace', 'nowrap');
  }

  /**
   *
   * @description handler when options and model change
   */
  private onOptionsAndModelChange(): void {
    if (this.to.multiple && _.isArray(this.formControl.value)) {
      // when option still empty but select has initial value then immediately put in options
      if ((!this.to.options?.length) && this.formControl.value?.length) {
        this.to.options = this.formControl.value;
      } else if (this.to.addInitialOption !== false && this.to.options?.length && this.to.initialValue?.length && this.formControl.value?.length) {
        let preparedValues: any[] = [];
        // we will check if current formControl value existed within options list
        // if not exist, then add it into select with disabled option
        // if exist then update formControl value to use correct option format
        for (const value of this.formControl.value) {
          if (this.formlyUtil.valueNotInOptions(this.to, value)) {
            // add disabled option and update value based on newly added option format
            value[<string>this.to.disabledProp] = true;
            this.to.options.unshift(value);
            this.formControl.patchValue(value);
            preparedValues = preparedValues.concat([value]);
          } else {
            // loop to list of options and compare with 'value' if exist then update value
            for (const opt of this.to.options) {
              if (compareFn.call(this, opt, value)) {
                preparedValues = preparedValues.concat([opt]);
                break;
              }
            }
          }
        }
        // set formControl value, add timeout here until ng-select options has been fully transformed
        setTimeout(() => {
          this.formControl.setValue(preparedValues);
        });
      }
    } else {
      // if there is no options but there is model value then add to initial option list
      if ((!this.to.options || !this.to.options.length) && this.formControl.value) {
        this.to.options = [ this.formControl.value ];
      } else if (this.to.addInitialOption !== false && this.to.options?.length && this.to.initialValue && this.formControl.value) {
        if (this.formlyUtil.valueNotInOptions(this.to)) {
          this.addDisabledOption();
        } else {
          for (const item of this.to.options) {
            if (compareFn.call(this, item, this.formControl.value)) {
              // options will be transformed to NgOption interface then we can fully set value available in the list
              // add timeout here until ng-select options has been fully transformed
              setTimeout(() => {
                this.formControl.setValue(item);
              });
              break;
            }
          }
        }
      }
      // this is for select element when it doesn't have initial value but value set after certain actions along the way
      // we should add options on it, this is already the case before
      else if (this.to.options?.length && !this.to.initialValue && this.formControl.value) {
        // check if programmatically set value is within the options
        if (this.formlyUtil.valueNotInOptions(this.to, this.formControl.value)) {
          // add programmatically set option value to select options
          this.to.options.unshift({
            ...this.formControl.value,
            disabled: true
          });
        }
      }
    }
  }

  /**
   *
   * @description add disabled option to select option
   */
  private addDisabledOption(): void {
    const value: any = _.clone(this.to.initialValue);
    if (typeof value === typeof this.to.options[ 0 ]) {
      if (_.isObject(this.to.options[ 0 ])) {
        // set disabled key here based on disabledProp
        value[ <string>this.to.disabledProp ] = true;
      }

      // add initial option to select options
      this.to.options.unshift(value);

      // we set model back to initial value because model value has been set to undefined by angular select
      // this is a workaround to prevent model from being changed when new options are added while the user has not changed anything on the form
      this.formControl.patchValue(value);

      // we need to add timeout here to let the new options be processed in the DOM
      setTimeout(() => {
        // set the initial value option to disabled because it doesn't exist in the list of options
        // this is only when items list is not object
        if (!_.isObject(value)) {
          const itemIndex: number = _.findIndex(this.ngSelectRef.itemsList.items, { value });
          const items: NgOption[] = _.clone(this.ngSelectRef.itemsList.items);

          // set disabled item
          items[itemIndex].disabled = true;
          // update items list for ng-select
          this.ngSelectRef.itemsList.setItems(items);
        }
      });
    }
  }

  /**
   *
   * @param optionValue {NgOption}
   * @param currentValue {any}
   * @description this is to compare option value with current form control value
   */
  public compareWithFunction = (optionValue: NgOption, currentValue: any): boolean => {
    return compareFn.call(this, optionValue?.value, currentValue);
  };

  /**
   * @param item {any}
   * @description use for trackBy options in select
   */
  public trackByFunction(item: any): any {
    if (this.to?.format) {
      if (this.to.noTrack) {
        return item;
      } else if (this.to.trackBy) {
        return item[ this.to.trackBy ];
      } else {
        return item[ 'id' ];
      }
    } else {
      return item || item.id;
    }
  }

  /**
   * @param selectedOption {NgOption | any}
   * @description format selected option when 'to.parseHtml' is true, this is to escape linebreak in HTML option label when option is selected
   */
  public formatSelectedOption = (selectedOption: NgOption | any): string => {
    return this.formattingService.removeHtmlBreaks(this.getLabel(selectedOption), ', ');
  };


  /**
   * @param item {NgOption | any}
   * @description get label to be displayed in option list
   */
  public getLabel(item: NgOption | any): string {
    if (this.to.groupProp) {
      // when groupProp is defined ngSelect not compatible to use formlySelectOptions pipe to transfrom into NgOption format
      return _.isString(this.to.labelProp)? item[ this.to.labelProp ] : this.to.labelProp(item);
    } else {
      return item.label;
    }
  }

  /**
   * @param value {undefined}
   * @description event fired when onClear button clicked
   */
  public onClear(value: undefined): void {
    this.formControl.setValue(value);
  }

  public ngOnDestroy(): void {
    // when element destroyed / page changed then popup dropdown should be close
    $('.ng-dropdown-panel').remove();
  }
}